import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import { mailtoHref, toErrorMessage, type MessageDraft } from './inquiriesApi';

const LANGUAGES = [
  { id: 'pl', name: 'polski' },
  { id: 'en', name: 'angielski' },
  { id: 'de', name: 'niemiecki' },
];

/**
 * Szkic wiadomości do klienta (26.10.2026, P3) — potwierdzenie możliwości
 * realizacji albo oferta. Wzorem zapytań do przewoźników: system
 * przygotowuje treść, człowiek ją poprawia, kopiuje albo otwiera w swojej
 * poczcie i oznacza „wysłane ręcznie” — to jego oświadczenie. Z systemu nic
 * nie wychodzi.
 */
export const MessageDraftDialog = ({
  open,
  title,
  load,
  onClose,
  onSent,
  sentTestId,
}: {
  open: boolean;
  title: string;
  load: (language: string) => Promise<MessageDraft>;
  onClose: () => void;
  onSent: (subject: string, body: string, language: string) => Promise<void>;
  sentTestId: string;
}) => {
  const [language, setLanguage] = useState('pl');
  const [draft, setDraft] = useState<MessageDraft | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    load(language)
      .then((d) => {
        if (!alive) return;
        setDraft(d);
        setSubject(d.subject);
        setBody(d.body);
        setError(null);
      })
      .catch((e) => alive && setError(toErrorMessage(e)));
    return () => {
      alive = false;
    };
    // `load` zmienia się z każdym renderem wołającego — szkic odświeżamy przy otwarciu i zmianie języka.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, language]);

  const copy = () => {
    void navigator.clipboard?.writeText(`${subject}\n\n${body}`).then(() => setCopied(true));
  };

  const markSent = () =>
    void (async () => {
      setBusy(true);
      try {
        await onSent(subject, body, language);
        onClose();
      } catch (e) {
        setError(toErrorMessage(e));
      } finally {
        setBusy(false);
      }
    })();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Szkic przygotował system. Popraw go, skopiuj albo otwórz w swojej poczcie i wyślij sam — potem oznacz
          „Wysłane ręcznie”. Z aplikacji nic nie wychodzi.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {draft && draft.missing.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            W treści brakuje: {draft.missing.join(', ')} — nie dopisaliśmy nic z domysłu.
          </Alert>
        )}
        {draft?.signaturePlaceholder && body.includes('[imię i nazwisko]') && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Wpisz swoje imię i nazwisko w podpisie (albo uzupełnij je w „Mój profil”).
          </Alert>
        )}
        <Box sx={{ display: 'grid', gap: 1 }}>
          <TextField
            select
            size="small"
            label="Język"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            sx={{ maxWidth: 200 }}
          >
            {LANGUAGES.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Temat" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <TextField
            label="Treść"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={10}
            slotProps={{ htmlInput: { 'data-testid': 'inquiry-message-body' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button startIcon={<ContentCopyIcon />} onClick={copy} disabled={!draft}>
          {copied ? 'Skopiowano' : 'Kopiuj'}
        </Button>
        <Button startIcon={<EmailIcon />} href={mailtoHref(subject, body)} disabled={!draft}>
          Otwórz w poczcie
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Zamknij</Button>
        <Button
          variant="contained"
          onClick={markSent}
          disabled={!draft || busy || !subject.trim() || !body.trim()}
          data-testid={sentTestId}
        >
          Wysłane ręcznie
        </Button>
      </DialogActions>
    </Dialog>
  );
};
